import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Form } from 'react-final-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import verificationBGImage from '../../assets/images/amazon/verificationBGImage.png';
import warningIcon from '../../assets/images/warning.png';
import TenantConfig from '../../config';

/** helpers */
import { composeValidators, email, max, required } from '../../helpers/validation';
import rudderanalytics from '../../service/rudderstack';

/** components */
import { FormInput } from '../FormElements';

const WarningBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  column-gap: 8px;
  margin-bottom: 12px;
  color: #f36a3f;
`;

const ResetPassword = ({ onSubmit, className }) => {
  const [errMsg, setErrMsg] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const formOptions = useMemo(() => {
    return {
      fields: [
        {
          label: 'Email',
          placeholder: 'Enter your email address',
          required: true,
          type: 'email',
          validate: composeValidators(
            max('Maximum length is 50 characters', 50),
            required("Email can't be empty"),
            email('Email is invalid')
          ),
          name: 'email',
        },
      ],
    };
  }, []);

  useEffect(() => {
    rudderanalytics.page('ForgotPassword');
  }, []);

  const onSubmitInternal = useCallback(
    async (values) => {
      try {
        await onSubmit(values);
        navigate('/auth/phone-password-recovery' + location.search);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          switch (err.response.data.errors[0].code) {
            case 'ENTITY_NOT_FOUND':
              setErrMsg('The user is not registered yet.');
              break;
            case 'USER_IS_DISABLED':
              setErrMsg('This account is locked.');
              break;

            default:
              setErrMsg('Please check your email.');
              break;
          }
        } else {
          setErrMsg('Please check your email.');
        }
      }
    },
    [navigate, location, onSubmit]
  );

  const scrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };

  return (
    <>
      {className === 'admin' && (
        <div className="logo-wrapper">
          <img className="logo" src={TenantConfig.headerLogo} alt="logo" />
        </div>
      )}
      <div
        className={`${
          className === 'admin' ? 'admin-form topoffset-resetpassword' : className
        } section`}
      >
        <div className={`${className}-header`}>
          <h1>{className === 'admin' ? 'Forgot password' : 'Reset password'}</h1>
        </div>
        {className === 'admin' && (
          <div className="admin-form__description-wrapper">
            <div className="admin-form__description">Please enter your email address</div>
            <div className="admin-divider" />
          </div>
        )}
        <div className={`${className}-main`}>
          <Form
            onSubmit={onSubmitInternal}
            render={({ handleSubmit, form, submitting, pristine, submitError }) => (
              <form onSubmit={handleSubmit} className="form">
                {formOptions.fields.map((f) => {
                  return <FormInput {...f} key={`form_${f.name}`} />;
                })}
                {errMsg && (
                  <WarningBox>
                    <img src={warningIcon} />
                    <span>{errMsg}</span>
                  </WarningBox>
                )}
                {className === 'admin' ? (
                  <div className="form-buttons-reset">
                    <button
                      className="btn primary-btn"
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Reset password
                    </button>
                    <button
                      className="btn secondary-btn"
                      type="button"
                      disabled={submitting}
                      onClick={() => navigate('/auth/sign-in' + location.search)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      className="btn primary-btn"
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Reset password
                    </button>
                    <button
                      className="btn secondary-btn"
                      type="button"
                      disabled={submitting}
                      onClick={() => navigate('/auth/sign-in' + location.search)}
                    >
                      Return to form
                    </button>
                  </>
                )}

                {submitError &&
                  (submitError == 'USER_IS_DISABLED' ? (
                    <div className={`${className}-error error-text`}>
                      <p>The account is locked out due to too many failed login attempts.</p>
                      <p>
                        Please{' '}
                        <a href="#contact" onClick={scrollToBottom}>
                          contact support
                        </a>
                        .{' '}
                      </p>
                    </div>
                  ) : (
                    <div className={className === 'admin' && 'reset-error'}>
                      <p className="error-text">{submitError}</p>
                    </div>
                  ))}
              </form>
            )}
          />
        </div>
        <p className="subtitle">
          {className === 'admin' ? (
            <small>
              Already have an account, <Link to={`/auth/sign-in?app=/${className}`}>Sign in</Link>
            </small>
          ) : (
            <small>
              <Link to="/auth/register">Don't have an account yet? Please sign up!</Link>
            </small>
          )}
        </p>
      </div>
      {TenantConfig?.auth.renderBottomImg && className !== 'admin' && (
        <img
          className="phone-verification-bg"
          src={verificationBGImage}
          alt="Verification Background Image"
        />
      )}
    </>
  );
};

export default ResetPassword;
